import React, { Component } from 'react';
import { connect } from 'react-redux';
import { func, string, number } from 'prop-types';

import { Decimal } from 'decimal.js';
import LoadingCube from 'sub_components/Common/LoadingCube';
import * as StorageUtils from 'utils/StorageUtils';
import { TICKET_TYPES, HIGH_LEVEL_TICKET_STATUS } from 'utils/TicketConstants';
import { RESOLUTIONS } from 'services/config';
import Highcharts from 'highcharts/highstock';
import GraphDetail from 'blox_components/Storage/StorageOverviewDetail/Components/GraphDetails';
import highcharts3d from 'highcharts/highcharts-3d';
highcharts3d(Highcharts);

class StorageDonut extends Component {
	constructor(props) {
		super(props);

		this.state = {
			tickets: [],
			error: null,
			formattedTicketData: [
				['Commitment', 0],
				['Usage', 1],
			],
			openTickets: 0,
			closedTickets: 0,
			show: false,
			loading: true,
			commitment: 0,
			usage: 0,
		};
	}

	calculateStartAngle = data => {
		if (!data) {
			return 0;
		}
		const open = data[0];
		const closed = data[1];
		const amountOpen = open[1];
		const amountClosed = closed[1];
		const totalTickets = amountOpen + amountClosed;
		const angle = (amountOpen / totalTickets) * 360;

		if (amountOpen === 0 && amountClosed === 0) {
			return 0;
		}

		return 90 - angle / 2;
	};
	getGraphSize() {
		const { breakpoint } = this.props;

		let width = 135;
		let height = 130;
		let innerSize = 60;
		let depth = 20;

		if (breakpoint === RESOLUTIONS.MED) {
			width = 173;
			height = 173;
			innerSize = 85;
			depth = 28;
		} else if (breakpoint === RESOLUTIONS.HIGH) {
			width = 253;
			height = 253;
			innerSize = 130;
			depth = 45;
		}

		return { width, height, innerSize, depth };
	}
	getModuleColors = (id, isOverQuota) => {
		switch (id) {
			case 'account':
				return ['#2a7de1', '#a2a9ad'];
			case 'support':
				return ['#a46cf4', '#a2a9ad'];
			case 'billing':
				return ['#b5d334', '#a2a9ad'];
			case 'storage':
				if (!isOverQuota) {
					return ['#00b0b9', '#a2a9ad'];
				} else {
					return ['#00b0b9', '#df6a2e'];
				}
			default:
				return ['#a46cf4', '#a2a9ad'];
		}
	};

	createDonut = (formattedTicketData, isOverQuota) => {
		const { id } = this.props;
		const startAngle = this.calculateStartAngle(formattedTicketData);
		const { width, height, innerSize, depth } = this.getGraphSize();
		const colors = this.getModuleColors('storage', isOverQuota);

		Highcharts.chart(`donut-container-${id}`, {
			chart: {
				width,
				height,
				type: 'pie',

				options3d: {
					enabled: true,
					alpha: 0,
					beta: 57,
				},
			},

			title: {
				text: '',
			},
			tooltip: {
				enabled: false,
			},
			plotOptions: {
				pie: {
					innerSize,
					depth,
					startAngle,
				},

				series: {
					colors,
					edgeColor: '#ffffff33',
					states: {
						inactive: {
							opacity: 1,
						},
					},
					dataLabels: {
						enabled: false,
					},
				},
			},

			series: [
				{
					name: '',
					data: formattedTicketData,
				},
			],
		});
	};
	calculateTotalUsage = (data, type, unit = null) => {
		let usageUnit;

		if (unit) {
			usageUnit = unit;
		} else if (data[type].length !== 0) {
			usageUnit =
				data[type].length === 1
					? StorageUtils.determineDataUnit(data, type)
					: data[type].reduce((type, item) => StorageUtils.determineDataUnit(item, type));
		}
		if (data[type] && data[type].length !== 0) {
			let totalUsage = data[type].reduce(
				// eslint-disable-next-line
				(total, location) => (total += location[`size${usageUnit}`]),
				0,
			);

			if (isNaN(totalUsage)) {
				return { totalUsage: 0, unit: 'mb' };
			} else {
				totalUsage = new Decimal(totalUsage);

				return { totalUsage, unit: usageUnit };
			}
		} else {
			return { totalUsage: 0, unit: 'mb' };
		}
	};
	getPercentageDetails = (isOverQuota, commitmentAmount, totalUsage) => {
		const percent = isOverQuota
			? (commitmentAmount / totalUsage) * 100
			: (totalUsage / commitmentAmount) * 100;

		return percent.toFixed(2);
	};
	calculatePercentage = (commitmentAmount, usage) => {
		const { data } = this.props;

		if (!data) return null;

		const isOverQuota = usage > commitmentAmount;
		const percentage = this.getPercentageDetails(isOverQuota, commitmentAmount, usage);
		const commitmentPercent = 100 - percentage;
		return { percentage: Number(percentage), isOverQuota, total: commitmentPercent };
	};
	componentDidUpdate(prevProps) {
		const { breakpoint, commitment, usage } = this.props;
		if (prevProps.breakpoint !== breakpoint || commitment !== prevProps.commitment) {
			const {
				percentage: usagePercentage,
				isOverQuota,
				total: commitPercentage,
			} = this.calculatePercentage(commitment, usage);
			let formattedTicketData = [
				['Usage', commitPercentage],
				['Commitment', usagePercentage],
			];

			if (isOverQuota) {
				formattedTicketData = [
					['Commitment', usagePercentage],
					['Usage', commitPercentage],
				];
			}

			const isOver = isOverQuota && commitment && commitment !== 0 ? true : false;
			if (commitment) {
				this.createDonut(formattedTicketData, isOver);
			}
			this.setState({
				incomingPercentage: usagePercentage,
				isOverQuota,
				formattedTicketData,
				loading: false,
				show: true,
				commitment,
				usage,
			});
		}
	}

	componentDidMount() {
		const { commitment, usage } = this.props;

		const {
			percentage: usagePercentage,
			isOverQuota,
			total: commitPercentage,
		} = this.calculatePercentage(commitment, usage);
		let formattedTicketData = [
			['Usage', commitPercentage],
			['Commitment', usagePercentage],
		];

		if (isOverQuota) {
			formattedTicketData = [
				['Commitment', usagePercentage],
				['Usage', commitPercentage],
			];
		}

		const isOver = isOverQuota && commitment && commitment !== 0 ? true : false;
		if (commitment) {
			this.createDonut(formattedTicketData, isOver);
		}
		this.setState({
			incomingPercentage: usagePercentage,
			isOverQuota,
			formattedTicketData,
			loading: false,
			show: true,
			commitment,
			usage,
		});
	}
	getCommitment = (commitment, unit) => {
		return (
			<div className='donut-title-bottom' onClick={() => {}} data-cy='th-open-link'>
				{commitment && unit && (
					<>
						<span className='number numbers7'>{`${commitment} ${unit}`}</span>
						<span className='text header52'>{`COMMIT`}</span>
					</>
				)}
			</div>
		);
	};

	render() {
		const { id, total, unit, data, packageData, commitment } = this.props;
		const { usage, show, loading, isOverQuota } = this.state;
		const noCommit = !commitment || commitment === 0 ? 'nocommit' : '';
		const overage = isOverQuota ? usage - commitment : null;

		if (commitment) {
			return (
				<>
					{loading && <LoadingCube />}
					<div
						className={
							total === 0 ? 'donut-chart-container zero-state' : `donut-chart-container ${id}`
						}
					>
						{show && (
							<div
								className={`donut-title-top ${noCommit}`}
								onClick={() => {}}
								data-cy='th-closed-link'
							>
								<span className='number numbers7'>
									{isOverQuota && overage ? `${overage.toFixed(0)} ${unit}` : ''}
								</span>
								<span className='text header52'>{isOverQuota ? 'OVERAGE' : ''}</span>
							</div>
						)}
						<div id={`donut-container-${id}`} onClick={() => {}} />
						{show && this.getCommitment(commitment, unit)}
					</div>
				</>
			);
		} else {
			return (
				<GraphDetail
					data={data}
					packageData={packageData}
					packageType={packageData?.packageType}
					id={'object'}
				/>
			);
		}
	}
}

StorageDonut.propType = {
	goToTicketHistory: func,
	showTitle: func,
	ticketType: string,
	id: string,
	breakpoint: number,
};

StorageDonut.defaultProps = {
	goToTicketHistory: () => {},
	showTitle: () => {},
	ticketType: TICKET_TYPES.SUPPORT,
	status: HIGH_LEVEL_TICKET_STATUS.OPEN,
	breakpoint: 1344,
};

function mapStateToProps(state) {
	return {
		auth_status: state.auth_status,
		company_info: state.company_info,
	};
}

export default connect(mapStateToProps, null)(StorageDonut);
